import _ from 'lodash';
import local_config from './local.json';
import local_remote_backend_config from './local-remote-backend.json';
import local_ssl_config from './local-ssl.json';
import local_ssl_one_domain_config from './local-ssl-one-domain.json';
import local_tailscale from './local-tailscale.json';
import mock_config from './mock.json';

import prod_config from './prod.json';
import test_config from './test.json';

import local_tunnelto_config from './local-tunnelto.json';


const _allowed_envs = [
  'prod',
  'test',
  'local',
  'local-ssl',
  'local-ssl-one-domain',
  'local-remote-backend',
  'local-tunnelto',
  'mock'
] as const;

export type _EnvsTuple = typeof _allowed_envs;
export type Env = _EnvsTuple[number];
export const ALLOWED_ENVS: ReadonlyArray<Env> = _allowed_envs;

const appEnvFullName = (process.env.APP_ENV || 'mock').trim();


export const appEnv: Env                   = (appEnvFullName.split('-')[0]) as Env;
export const dev                           = appEnv === 'prod' ? false : process.env.NODE_ENV !== 'production';
export const appSubenv: string | undefined = process.env.APP_SUBENV ?? (appEnvFullName.split('-').slice(1).join('-'));
export const APP_VERSION                   = process.env.APP_VERSION || 'unknown';
export const RELEASE_VERSION               = process.env.RELEASE_VERSION || 'unknown';

if (!ALLOWED_ENVS.includes(appEnv)) {
  throw new Error(`process.env.APP_ENV must be one of: ${ALLOWED_ENVS.join(', ')}, but got '${process.env.APP_ENV}'`);
}

// console.log(`appEnv = ${appEnv} -- ${process.env.APP_ENV}`)
export const buildAppConfig: AppConfig = loadAppConfig(appEnv);

export function loadAppConfig(env: Env): AppConfig {
  const effective_config: AppConfig = {
    ...prod_config,
    env: 'mock',
    dev: dev,
    version: APP_VERSION,
    releaseVersion: RELEASE_VERSION,
  };

  if (env === 'prod') {
    effective_config.env = 'prod';
  } else if (env === 'test') {
    _.merge(effective_config, test_config);
    effective_config.env = 'test';
  } else if (env === 'local') {
    _.merge(effective_config, test_config);
    _.merge(effective_config, local_config);

    switch(appSubenv) {
      case 'tunnelto': {
        _.merge(effective_config, local_tunnelto_config);
        break;
      }
      case 'remote-backend': {
        _.merge(effective_config, local_remote_backend_config);
        break;
      }
      case 'ssl': {
        _.merge(effective_config, local_ssl_config);
        break;
      }
      case 'ssl-one-domain': {
        _.merge(effective_config, local_ssl_one_domain_config);
        break;
      }
      case 'tailscale': {
        _.merge(effective_config, local_tailscale);
        break;
      }
    }

    effective_config.env = 'local';
  } else if (env === 'local-tunnelto') {
    _.merge(effective_config, test_config);
    _.merge(effective_config, local_config);
    effective_config.env = 'local';
  } else if (env === 'mock') {
    _.merge(effective_config, test_config);
    _.merge(effective_config, local_config);
    _.merge(effective_config, mock_config);
    effective_config.env = 'mock';
  } else {
    throw new Error(`Invalid env: ${env}`);
  }
  return effective_config;
}


export interface AppConfig {
  env: Env;
  dev: boolean;
  version: string;
  releaseVersion: string;

  name: string;
  code: string;
  frontendUrl: string;
  homepageUrl: string;
  defaultFacebookImageUrl: string;
  showBanner: boolean;
  rejestracjaPodopiecznegoEnabled: boolean;
  edycjaPodopiecznego: boolean;
  favicon: Favicon;
  backendUrl: string;
  apiUrl: string;
  extensionUrls: ExtensionUrls;
  description: string;
  socialSharing: SocialSharing;
  auth: {
    homeRoute: string
  },
  shopDefaultLogoUrl: string
  config: {
    gtm: {
      id: string
      enabled?: boolean
    }
    cookiebot: {
      enabled: boolean
      cbid: string | null
    }
    ipresso: {
      enabled?: boolean
    },
    sentry: {
      enabled: boolean
      dsn: string | null
      tracesSampleRate: number
      organizationSlug: string
      projectsSlugs: string[]
      projectSlug: string | null
    }
  }
}

export interface ExtensionUrls {
  chrome: string;
  firefox: string;
  edge: string;
}


interface Favicon {
  ext: string;
  mimeType: string;
}


interface SocialSharing {
  title: string;
  description: string;
  hashtags: string[];
}
